import { CloudFortifiedStatusValues } from '@atlassian/mpac-types';
import { List, Map, OrderedMap } from 'immutable';
import { CLOUD, DATA_CENTER, SERVER } from '../constants/hostings';
import { BAMBOO, BITBUCKET, CONFLUENCE, CROWD, FECRU, JIRA, JIRA_SERVICE_DESK, TRELLO } from '../constants/products';
import { CONJUNCTIVE_FACET_TYPE, DISJUNCTIVE_FACET_TYPE } from '../records/FacetType';
import algoliaFacets from './algolia-facets';
import categoriesRank from './categoriesRank';
import { sortOptions } from './sorting';
const categoriesByUsage = categoriesRank.sortedByUsage.map(categoryName => categoryName.toLowerCase());
export const sortingLogic = {
  USAGE_AND_ALPHABETICAL: 'usage-and-alphabetical',
  ALPHABETICAL: 'alphabetical',
  COUNT_AND_ALPHABETICAL: 'count-and-alphabetical',
  DEFAULT: 'default'
};
export const FreeStarterTierFilterValues = Object.freeze({
  enabled: 'enabled',
  disabled: 'disabled'
});
function resolveFacetProductCount(facets, facetKey, facetProductKey) {
  const facet = facets.find(f => f.get('name') === facetKey);
  return facet ? facet.getIn(['data', facetProductKey]) || 0 : 0;
}
function sortByCountDesc(a, b) {
  if (b.get('count') - a.get('count') !== 0) {
    return b.get('count') - a.get('count');
  }
  return a.get('label').localeCompare(b.get('label'));
}
const sortByUsage = (a, b) => {
  const getIndex = element => !categoriesByUsage.includes(element.toLowerCase()) ? Number.POSITIVE_INFINITY : categoriesByUsage.indexOf(element.toLowerCase());
  return getIndex(a.get('label')) - getIndex(b.get('label'));
};

// eslint-disable-next-line max-lines-per-function -- Fix me
const productFilter = function productFilter() {
  var _options$featureFlags, _options$featureFlags2;
  let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const customFilters = {
    [JIRA]: Map({
      value: JIRA,
      algoliaValue: 'jira',
      label: 'Jira',
      resolveFacetCount(facets) {
        return resolveFacetProductCount(facets, productFilter().get('algoliaFacetKey'), 'jira');
      }
    }),
    [JIRA_SERVICE_DESK]: Map({
      value: JIRA_SERVICE_DESK,
      algoliaValue: 'jira-service-desk',
      label: options !== null && options !== void 0 && (_options$featureFlags = options.featureFlags) !== null && _options$featureFlags !== void 0 && _options$featureFlags.enableJsdToJsm ? 'Jira Service Management' : 'Jira Service Desk',
      resolveFacetCount(facets) {
        return resolveFacetProductCount(facets, productFilter().get('algoliaFacetKey'), 'jira-service-desk');
      }
    }),
    [CONFLUENCE]: Map({
      algoliaValue: 'confluence',
      value: CONFLUENCE,
      label: 'Confluence',
      resolveFacetCount(facets) {
        return resolveFacetProductCount(facets, productFilter().get('algoliaFacetKey'), 'confluence');
      }
    }),
    [BITBUCKET]: Map({
      algoliaValue: 'bitbucket',
      value: BITBUCKET,
      label: 'Bitbucket',
      resolveFacetCount(facets) {
        return resolveFacetProductCount(facets, productFilter().get('algoliaFacetKey'), 'bitbucket');
      }
    }),
    [FECRU]: Map({
      algoliaValue: 'fecru',
      value: FECRU,
      label: 'Fisheye/Crucible',
      resolveFacetCount(facets) {
        return resolveFacetProductCount(facets, productFilter().get('algoliaFacetKey'), 'fecru');
      }
    }),
    [BAMBOO]: Map({
      algoliaValue: 'bamboo',
      value: BAMBOO,
      label: 'Bamboo',
      resolveFacetCount(facets) {
        return resolveFacetProductCount(facets, productFilter().get('algoliaFacetKey'), 'bamboo');
      }
    }),
    [CROWD]: Map({
      algoliaValue: 'crowd',
      value: CROWD,
      label: 'Crowd',
      resolveFacetCount(facets) {
        return resolveFacetProductCount(facets, productFilter().get('algoliaFacetKey'), 'crowd');
      }
    })
  };
  if ((options === null || options === void 0 ? void 0 : (_options$featureFlags2 = options.featureFlags) === null || _options$featureFlags2 === void 0 ? void 0 : _options$featureFlags2.showTrelloProductFilter) !== false) {
    customFilters[TRELLO] = Map({
      algoliaValue: 'trello',
      value: TRELLO,
      label: 'Trello',
      resolveFacetCount(facets) {
        return resolveFacetProductCount(facets, productFilter().get('algoliaFacetKey'), 'trello');
      }
    });
  }
  return Map({
    key: 'product',
    label: 'Product',
    sidebarLabel: 'All Products',
    topBarLabel: 'Products',
    algoliaFacetKey: algoliaFacets.getIn(['product', 'algoliaFacetKey']),
    queryType: DISJUNCTIVE_FACET_TYPE,
    type: CONJUNCTIVE_FACET_TYPE,
    primary: true,
    custom: true,
    displayInCurrent: true,
    facet: true,
    discoverFilter: true,
    requiredDiscoverFilterWhenNonEmpty: true,
    useDropDownComponentForSidebar: true,
    resolveAlgoliaQuery(_ref) {
      let {
        params,
        filters
      } = _ref;
      const product = filters.get('product');
      return product ? params.addDisjunctiveFacetRefinement(productFilter().get('algoliaFacetKey'), productFilter().getIn(['customFilters', product, 'algoliaValue'])) : params;
    },
    customFilters: OrderedMap(customFilters)
  });
};
const hostingFilter = () => Map({
  key: 'hosting',
  label: 'Select a hosting type',
  sidebarLabel: 'Select a hosting type',
  topBarLabel: 'Hosting',
  algoliaFacetKey: algoliaFacets.getIn(['hosting', 'algoliaFacetKey']),
  queryType: DISJUNCTIVE_FACET_TYPE,
  type: CONJUNCTIVE_FACET_TYPE,
  custom: true,
  displayInCurrent: true,
  facet: true,
  openByDefault: true,
  useDropDownComponentForSidebar: true,
  resolveAlgoliaQuery(_ref2) {
    let {
      params,
      filters
    } = _ref2;
    const customFilter = hostingFilter().getIn(['customFilters', filters.get('hosting')]);
    return customFilter ? params.addFacetRefinement(algoliaFacets.getIn(['latestOfHosting', 'algoliaFacetKey']), 'true').addDisjunctiveFacetRefinement(hostingFilter().get('algoliaFacetKey'), customFilter.get('value')) : params.addFacetRefinement(algoliaFacets.getIn(['canonicalVersion', 'algoliaFacetKey']), 'true');
  },
  customFilters: Map({
    cloud: Map({
      algoliaValue: CLOUD,
      value: CLOUD,
      label: 'Cloud',
      resolveFacetCount(facets, filters) {
        const facetKey = filters.get('hosting') ? hostingFilter().get('algoliaFacetKey') : algoliaFacets.getIn(['deployments', 'algoliaFacetKey']);
        return resolveFacetProductCount(facets, facetKey, CLOUD);
      }
    }),
    server: Map({
      algoliaValue: SERVER,
      value: SERVER,
      label: 'Server',
      resolveFacetCount(facets, filters) {
        const facetKey = filters.get('hosting') ? hostingFilter().get('algoliaFacetKey') : algoliaFacets.getIn(['deployments', 'algoliaFacetKey']);
        return resolveFacetProductCount(facets, facetKey, SERVER);
      }
    }),
    dataCenter: Map({
      algoliaValue: DATA_CENTER,
      value: DATA_CENTER,
      label: 'Data Center',
      resolveFacetCount(facets, filters) {
        const facetKey = filters.get('hosting') ? hostingFilter().get('algoliaFacetKey') : algoliaFacets.getIn(['deployments', 'algoliaFacetKey']);
        return resolveFacetProductCount(facets, facetKey, DATA_CENTER);
      }
    })
  })
});
const categoryFilter = () => Map({
  key: 'category',
  algoliaFacetKey: algoliaFacets.getIn(['categories', 'algoliaFacetKey']),
  label: 'Categories',
  queryType: DISJUNCTIVE_FACET_TYPE,
  type: CONJUNCTIVE_FACET_TYPE,
  displayInCurrent: true,
  facet: true,
  primary: true,
  resolveAlgoliaQuery(_ref3) {
    let {
      params,
      filters
    } = _ref3;
    const category = filters.get('category');
    return category ? params.addDisjunctiveFacetRefinement(algoliaFacets.getIn(['categories', 'algoliaFacetKey']), category) : params;
  },
  // MC-731 Experiment when sorting logic is usage-and-alphabetical, render in groups of popular and all
  collateFunction(facetProducts, sortLogic) {
    if (sortLogic === sortingLogic.USAGE_AND_ALPHABETICAL) {
      const firstFiveByUsage = facetProducts.slice(0, 5);
      const restByLabel = facetProducts.slice(5);
      return {
        popular: firstFiveByUsage,
        all: restByLabel
      };
    }
    return facetProducts;
  },
  sortFunction(facetProducts, sortLogic) {
    const productsByUsage = facetProducts.sort(sortByUsage);
    const productsByLabel = facetProducts.sortBy(valA => valA.get('label'));
    const productsByAppCount = facetProducts.sort(sortByCountDesc);
    switch (sortLogic) {
      case sortingLogic.ALPHABETICAL:
        return productsByLabel;
      case sortingLogic.USAGE_AND_ALPHABETICAL:
        {
          const firstFiveByUsage = productsByUsage.slice(0, 5);
          const restByLabel = productsByUsage.sortBy(valA => valA.get('label'));
          return firstFiveByUsage.concat(restByLabel);
        }
      case sortingLogic.COUNT_AND_ALPHABETICAL:
        {
          const firstFiveByCount = productsByAppCount.slice(0, 5);
          const restByLabel = productsByAppCount.slice(5).sortBy(valA => valA.get('label'));
          return firstFiveByCount.concat(restByLabel);
        }
      default:
        return facetProducts;
    }
  }
});
const freeCustomFilter = Map({
  label: 'Free for all teams',
  value: 'free',
  algoliaFacetKey: algoliaFacets.getIn(['paid', 'algoliaFacetKey']),
  queryType: CONJUNCTIVE_FACET_TYPE,
  resolveFacetCount(facets) {
    return resolveFacetProductCount(facets, freeCustomFilter.get('algoliaFacetKey'), 'false');
  },
  resolveAlgoliaQuery(_ref4) {
    let {
      params,
      moreFilters
    } = _ref4;
    return moreFilters.includes(freeCustomFilter.get('value')) ? params.addFacetRefinement(freeCustomFilter.get('algoliaFacetKey'), 'false') : params;
  }
});
const ecosystemCloudAppsFilter = () => Map({
  key: 'nonEcosystemCloudApps',
  label: 'Non Ecosystem Cloud Apps',
  value: 'nonEcosystemCloudApps',
  algoliaFacetKey: algoliaFacets.getIn(['pluginSystemVersion', 'algoliaFacetKey']),
  queryType: CONJUNCTIVE_FACET_TYPE,
  resolveAlgoliaQuery(_ref5) {
    let {
      params
    } = _ref5;
    return params;
  }
});
const vendorSupportedCustomFilter = Map({
  label: 'Partner supported',
  value: 'vendorSupported',
  algoliaFacetKey: algoliaFacets.getIn(['vendorSupported', 'algoliaFacetKey']),
  queryType: CONJUNCTIVE_FACET_TYPE,
  resolveFacetCount(facets) {
    return resolveFacetProductCount(facets, vendorSupportedCustomFilter.get('algoliaFacetKey'), 'true');
  },
  resolveAlgoliaQuery(_ref6) {
    let {
      params,
      moreFilters
    } = _ref6;
    return moreFilters.includes(vendorSupportedCustomFilter.get('value')) ? params.addFacetRefinement(vendorSupportedCustomFilter.get('algoliaFacetKey'), 'true') : params;
  }
});
const betaVersionCustomFilter = Map({
  label: 'Beta version',
  value: 'betaVersion',
  algoliaFacetKey: algoliaFacets.getIn(['stable', 'algoliaFacetKey']),
  queryType: CONJUNCTIVE_FACET_TYPE,
  resolveFacetCount(facets) {
    return resolveFacetProductCount(facets, betaVersionCustomFilter.get('algoliaFacetKey'), 'false');
  },
  resolveAlgoliaQuery(_ref7) {
    let {
      params,
      moreFilters
    } = _ref7;
    return moreFilters.includes(betaVersionCustomFilter.get('value')) ? params.addFacetRefinement(betaVersionCustomFilter.get('algoliaFacetKey'), 'false') : params;
  }
});
const cloudFortifiedAppCustomFilter = Map({
  key: 'cloudFortified',
  label: 'Cloud Fortified apps',
  value: 'cloudFortified',
  algoliaFacetKey: algoliaFacets.getIn(['cloudFortified', 'algoliaFacetKey']),
  queryType: CONJUNCTIVE_FACET_TYPE,
  resolveFacetCount(facets) {
    return resolveFacetProductCount(facets, cloudFortifiedAppCustomFilter.get('algoliaFacetKey'), CloudFortifiedStatusValues.Approved);
  },
  resolveAlgoliaQuery(_ref8) {
    let {
      params,
      moreFilters
    } = _ref8;
    return moreFilters.includes(cloudFortifiedAppCustomFilter.get('value')) ? params.addFacetRefinement(cloudFortifiedAppCustomFilter.get('algoliaFacetKey'), CloudFortifiedStatusValues.Approved) : params;
  }
});
const freeStarterTierFilter = Map({
  key: 'freeStarterTier',
  label: 'Free up to 10 users',
  value: 'freeStarterTier',
  algoliaFacetKey: algoliaFacets.getIn(['freeStarterTier', 'algoliaFacetKey']),
  queryType: CONJUNCTIVE_FACET_TYPE,
  resolveFacetCount(facets) {
    return resolveFacetProductCount(facets, freeStarterTierFilter.get('algoliaFacetKey'), FreeStarterTierFilterValues.enabled);
  },
  resolveAlgoliaQuery(_ref9) {
    let {
      params,
      moreFilters
    } = _ref9;
    return moreFilters.includes(freeStarterTierFilter.get('value')) ? params.addFacetRefinement(freeStarterTierFilter.get('algoliaFacetKey'), FreeStarterTierFilterValues.enabled) : params;
  }
});
const moreCustomFilters = OrderedMap({
  cloudFortified: cloudFortifiedAppCustomFilter,
  vendorSupported: vendorSupportedCustomFilter,
  free: freeCustomFilter,
  betaVersion: betaVersionCustomFilter,
  freeStarterTier: freeStarterTierFilter
});
const moreFiltersFilter = () => Map({
  key: 'moreFilters',
  label: 'Filter By',
  sidebarLabel: 'Filter By',
  topBarLabel: 'More Filters',
  type: DISJUNCTIVE_FACET_TYPE,
  custom: true,
  displayInCurrent: true,
  facet: true,
  primary: true,
  resolveAlgoliaQuery(_ref10) {
    let {
      params,
      filters
    } = _ref10;
    const moreFilters = filters.get('moreFilters');
    return moreFiltersFilter().get('customFilters').reduce((p, cf) => cf.get('resolveAlgoliaQuery')({
      params: p,
      moreFilters
    }), params);
  },
  customFilters: moreCustomFilters
});
const queryFilter = Map({
  key: 'query',
  facet: false,
  displayInCurrent: true,
  resolveCurrentFilterText(_ref11) {
    let {
      value
    } = _ref11;
    return "\"".concat(value, "\"");
  },
  resolveAlgoliaQuery(_ref12) {
    let {
      params,
      filters
    } = _ref12;
    return params.setQuery(filters.get('query') || '');
  }
});
const marketingLabel = Map({
  algoliaFacetKey: algoliaFacets.getIn(['marketingLabels', 'algoliaFacetKey']),
  key: 'marketingLabel',
  queryType: DISJUNCTIVE_FACET_TYPE,
  type: DISJUNCTIVE_FACET_TYPE,
  facet: false,
  displayInCurrent: true,
  resolveCurrentFilterText(_ref13) {
    let {
      value
    } = _ref13;
    return value;
  },
  resolveAlgoliaQuery(_ref14) {
    let {
      params,
      filters
    } = _ref14;
    return filters.get('marketingLabel').reduce((p, c) => p.addDisjunctiveFacetRefinement(algoliaFacets.getIn(['marketingLabels', 'algoliaFacetKey']), c), params);
  }
});
const sortFilter = () => Map({
  key: 'sort',
  facet: true,
  label: 'Sort By',
  sidebarLabel: 'Sort By',
  topBarLabel: 'Sort',
  displayInCurrent: false,
  type: CONJUNCTIVE_FACET_TYPE,
  primary: true,
  custom: true,
  nonRetrievableAttribute: true,
  useDropDownComponentForSidebar: true,
  resolveAlgoliaQuery(_ref15) {
    let {
      params,
      sort
    } = _ref15;
    const applyOverrides = sort && sort.get('applyOverrides') || (q => q);
    return applyOverrides(params);
  },
  customFilters: sortOptions.reduce((acc, so) => acc.set(so.get('key'), Map({
    value: so.get('key'),
    label: so.get('label')
  })), OrderedMap())
});
export const getFilters = function getFilters() {
  let Config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
    featureFlags: {}
  };
  const {
    featureFlags = {}
  } = Config;
  return List([ecosystemCloudAppsFilter(featureFlags), productFilter({
    featureFlags
  }), hostingFilter(), sortFilter(), moreFiltersFilter(), categoryFilter(), queryFilter, marketingLabel]).reduce((acc, f) => acc.set(f.get('key'), f), Map());
};
export const getTabletMobileFilters = function getTabletMobileFilters() {
  let Config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
    featureFlags: {}
  };
  const {
    featureFlags = {}
  } = Config;
  return List([ecosystemCloudAppsFilter(), sortFilter(), productFilter({
    featureFlags
  }), hostingFilter(), categoryFilter(), moreFiltersFilter(), queryFilter, marketingLabel]).reduce((acc, f) => acc.set(f.get('key'), f), Map());
};